import { getLandingEditorApiOrigin, isLandingEditorApiConfigured } from './landing-editor-api';
import { getSupabaseClient } from './supabase';

export interface SiteEditRequest {
  siteId: string;
  instruction: string;
  page?: string;
  maxSteps?: number;
}

export interface SiteEditResponse {
  ok?: boolean;
  replaced?: number;
  verified?: boolean;
  screenshot_url?: string;
  error?: string;
  trace_id?: string;
}

async function buildAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const supabase = getSupabaseClient();
  if (!supabase) return headers;
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

/** POST `/api/sites/:siteId/edit` — virtual FS edit executor (Stage 08). */
export async function postSiteEdit(request: SiteEditRequest): Promise<SiteEditResponse> {
  if (!isLandingEditorApiConfigured()) {
    throw new Error('VITE_LANDI_AGENTS_URL is not configured');
  }
  const origin = getLandingEditorApiOrigin();
  const headers = await buildAuthHeaders();
  const response = await fetch(
    `${origin}/api/sites/${encodeURIComponent(request.siteId)}/edit`,
    {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        instruction: request.instruction,
        page: request.page ?? 'index.html',
        max_steps: request.maxSteps ?? 6,
      }),
    },
  );

  const body = (await response.json()) as SiteEditResponse;
  if (!response.ok) {
    throw new Error(body.error ?? `Edit failed (${response.status})`);
  }
  return body;
}

export interface SitePublishResponse {
  ok?: boolean;
  url?: string;
  deploymentId?: string;
  error?: string;
}

/** POST `/api/publish` — draft → Cloudflare published site (Stage 08). */
export async function postSitePublish(request: {
  siteId: string;
  idempotencyKey?: string;
}): Promise<SitePublishResponse> {
  if (!isLandingEditorApiConfigured()) {
    throw new Error('VITE_LANDI_AGENTS_URL is not configured');
  }
  const origin = getLandingEditorApiOrigin();
  const headers = await buildAuthHeaders();
  const idempotencyKey = request.idempotencyKey ?? crypto.randomUUID();

  const response = await fetch(`${origin}/api/publish`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      siteId: request.siteId,
      idempotencyKey,
      scope: 'site',
    }),
  });

  const body = (await response.json()) as {
    ok?: boolean;
    data?: { url?: string; deploymentId?: string };
    error?: string;
    message?: string;
  };

  if (!response.ok) {
    throw new Error(body.error ?? body.message ?? `Publish failed (${response.status})`);
  }

  return {
    ok: body.ok ?? true,
    url: body.data?.url,
    deploymentId: body.data?.deploymentId,
  };
}
